import { fromVisioBasket } from '@agent/core/basket';
import { IBasketStore } from '@agent/core/basket/basket.store';
import { BasketCategoryType, BasketResult } from '@agent/core/basket/models/basket';
import {
  AgentFilter,
  AppointmentTypeFilter,
  BasketFilter,
  ContactFilter,
  ParticipantFilter,
  PeriodFilter,
  SkillsFilter,
} from '@agent/core/basket/visio/models/basket-filter';
import { QueryRequest } from '@agent/core/basket/visio/models/basket-request';
import { AppointmentType, QueryFilterModel, RequestModel, VisioChannelModel } from '@agent/core/basket/visio/visio-basket.model';
import { Criteria, FieldList, FilterOperator } from '@agent/core/channel-digit/visio/models/visio-request-queries';
import { BasketVisioLabels, IVisioRequest } from '@agent/core/channel-digit/visio/models/visio.requests.interfaces';
import { VisioService } from '@agent/core/channel-digit/visio/visio.service';
import { combineLatest, EMPTY, iif, map, merge, Observable, startWith } from 'rxjs';

import { ChannelType } from '@contact/upgrade';

const MODULE_NAME = 'visioBasketService';
const FIVE_MINUTES = 5 * 60 * 1000;

export interface IVisioBasketService {
  /**
   * compute counter when user is not on basket view
   */
  globalCounter(): Observable<BasketResult>;

  /**
   * compute counter for basket view
   */
  basketCounter(channelModel: VisioChannelModel): Observable<BasketResult>;

  /**
   *  compute total counter
   */
  getTotalCounter(): Observable<number>;

  /**
   * load requests basket with given configuration
   */
  getRequests(query: QueryRequest): Observable<BasketResult<RequestModel>>;
}

export class VisioBasketService implements IVisioBasketService {
  static $inject: Array<string> = ['basketStore', 'visioService'];

  public name = MODULE_NAME;

  constructor(private readonly basketStore: IBasketStore, private readonly visioService: VisioService) {}

  public globalCounter(): Observable<BasketResult> {
    return this.getCounter(BasketVisioLabels.ASSIGNABLE);
  }

  public basketCounter(channelModel: VisioChannelModel): Observable<BasketResult> {
    return iif(
      () => channelModel.categorySelected === BasketCategoryType.PERSONAL,
      this.personalCounter(channelModel),
      this.commonCounter(channelModel)
    );
  }

  public getTotalCounter(): Observable<number> {
    let visioPersonal$ = this.basketStore.pipe(fromVisioBasket.selectLabelTotalCountByType(ChannelType.VISIO, BasketVisioLabels.ALL));
    let visioCommon$ = this.basketStore.pipe(fromVisioBasket.selectLabelTotalCountByType(ChannelType.VISIO, BasketVisioLabels.ASSIGNABLE));
    return combineLatest(visioCommon$, visioPersonal$).pipe(
      map(values => values.reduce((acc, v) => acc + v, 0)),
      startWith(0)
    );
  }

  public getRequests({ label, currentPage, pageSize, sort, queryFilters }: QueryRequest): Observable<BasketResult<RequestModel>> {
    let rangeFilterPeriod: Criteria | undefined;
    let filter: FieldList | undefined;
    if (queryFilters != null) {
      filter = this.buildFilter(queryFilters);
      if (queryFilters.isPeriodFilterEnabled) {
        rangeFilterPeriod = new PeriodFilter(queryFilters.beginTime, queryFilters.endTime).build();
      }
    }
    let from = (currentPage - 1) * pageSize;
    return this.visioService.getRequests(filter, rangeFilterPeriod, label, pageSize, sort, from).pipe(
      map(nbRequests => ({
        label: label,
        filtered: queryFilters.hasFilters,
        nbRequests: nbRequests.total,
        requests: nbRequests.requests.map(it => this.toBasketRequest(it)),
      }))
    );
  }

  private personalCounter(channelModel: VisioChannelModel) {
    return merge(
      this.getCounterChecked(BasketVisioLabels.REQUESTED, channelModel),
      this.getCounterChecked(BasketVisioLabels.CANCELLED, channelModel),
      this.getCounterChecked(BasketVisioLabels.CONFIRMED, channelModel),
      this.getCounterChecked(BasketVisioLabels.REQUEST_CANCELLED, channelModel),
      this.getCounterChecked(BasketVisioLabels.ALL, channelModel),
      this.getCounter(BasketVisioLabels.ASSIGNABLE)
    );
  }

  private commonCounter(channelModel: VisioChannelModel) {
    return this.getCounterChecked(BasketVisioLabels.ASSIGNABLE, channelModel);
  }

  private getCounterChecked(label: BasketVisioLabels, channelModel: VisioChannelModel): Observable<BasketResult> {
    return channelModel.labelSelected !== label || channelModel.queryFilters.hasFilters ? this.getCounter(label) : EMPTY;
  }

  private getCounter(label: BasketVisioLabels): Observable<BasketResult> {
    return this.visioService.getRequests(undefined, undefined, label, 0, undefined).pipe(
      map(nbRequests => ({
        label: label,
        filtered: false,
        nbRequests: nbRequests.total,
      }))
    );
  }

  private buildFilter(queryFilters: QueryFilterModel): FieldList | undefined {
    let filters: BasketFilter[] = [
      new AppointmentTypeFilter(queryFilters.appointmentType),
      new ContactFilter(queryFilters.contact.toLowerCase()),
      new AgentFilter(queryFilters.agent.toLowerCase()),
      new ParticipantFilter(queryFilters.participant.toLowerCase()),
      new SkillsFilter(queryFilters.skills.map(it => it.id)),
    ].filter(value => !value.isEmpty());
    return queryFilters.hasFilters
      ? new FieldList(
          FilterOperator.MUST,
          filters.map(criteria => criteria.build())
        )
      : undefined;
  }

  private toBasketRequest(source: IVisioRequest): RequestModel {
    let obj = {};
    obj['id'] = source.uuid;
    obj['selected'] = false;
    obj['error'] = false;
    obj['type'] = source.type === 'INVITE' ? AppointmentType.INVITE : AppointmentType.APPOINTMENT;
    obj = { ...obj, ...source, startDate: new Date(source.startDate), endDate: new Date(source.endDate) };
    obj['isOperational'] = obj['startDate'].getTime() - new Date().getTime() < FIVE_MINUTES;
    return obj as RequestModel;
  }
}

export default MODULE_NAME;
